/**
 * Hilfe-Panel mit Tour, Tastatur-Shortcuts, Glossar.
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon.tsx';

const SHORTCUTS = [
  { key: '⌘K / Strg+K', desc: 'Schnellsuche öffnen' },
  { key: '/', desc: 'Filter-Suche fokussieren' },
  { key: 'Esc', desc: 'Dialog/Popup schließen' },
];

const GLOSSAR = [
  { term: 'Bon', desc: 'Ein Kassenbon (Kaufvorgang) — entspricht einer Zahlung an der Kasse.' },
  { term: 'Storno', desc: 'Stornierter Bon (Aufhebung). Wirkt sich nicht auf den Umsatz aus.' },
  { term: 'Geparkter Bon', desc: 'Bon zwischengespeichert, noch nicht abgeschlossen.' },
  { term: 'Ø-Bon', desc: 'Durchschnitts-Umsatz pro Verkaufsbon (Brutto).' },
  { term: 'Warengruppe', desc: 'Hauptkategorie eines Artikels (z. B. „Fleisch", „Obst/Gemüse").' },
  { term: 'Periode A / B', desc: 'Im Periodenvergleich: zwei beliebige Zeiträume parallel.' },
];

const TIPPS = [
  {
    title: 'Klick = Filter',
    desc: 'Jeder Balken, jede Tabellenzeile, jede Heatmap-Zelle setzt einen Filter. Alle anderen Charts reagieren sofort.',
  },
  {
    title: 'Filter speichern',
    desc: 'Setze deine Filter und klick in der Sidebar auf ★. Häufig genutzte Kombis pinnen.',
  },
  {
    title: 'Berichte mit einem Klick',
    desc: 'Unter „Berichte" gibt es vorgefertigte Auswertungen — z. B. „Letzte 7 Tage" oder „Samstag-Analyse".',
  },
  {
    title: 'Periodenvergleich',
    desc: 'Auf „Vergleich" klicken: zwei beliebige Zeiträume parallel auswerten (z. B. KW21 vs Vorjahr-KW21).',
  },
  {
    title: 'Export überall',
    desc: 'Jede Tabelle hat oben rechts einen Export-Button. CSV, Excel (.xlsx) oder PDF (Querformat).',
  },
  {
    title: 'Filial-Zugriff',
    desc: 'Das Badge im Header zeigt, auf wie viele Filialen du Zugriff hast. Klick = Liste anzeigen.',
  },
];

export function HelpPanel() {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="console-icon-btn"
        title="Hilfe & Tipps"
      >
        <span className="font-bold text-base">?</span>
      </button>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-start justify-center pt-12 px-4 animate-fade-in"
            onClick={() => setOpen(false)}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-white dark:bg-slate-900 rounded-xl shadow-elevated border border-slate-200 dark:border-slate-700 animate-slide-up"
            >
              <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between sticky top-0 bg-white/90 dark:bg-slate-900/90 backdrop-blur z-10">
                <div>
                  <h2 className="text-lg font-bold">Hilfe & Tipps</h2>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Wie du das Beste aus der Statistik holst
                  </p>
                </div>
                <button
                  className="btn-icon"
                  onClick={() => setOpen(false)}
                  title="Schließen (Esc)"
                >
                  <Icon name="x" size={18} />
                </button>
              </div>

              <div className="px-5 py-4 space-y-6">
                <section>
                  <h3 className="text-2xs uppercase tracking-wider font-semibold text-slate-500 mb-2">
                    Erste Schritte
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {TIPPS.map((t) => (
                      <div
                        key={t.title}
                        className="px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-800"
                      >
                        <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {t.title}
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                          {t.desc}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xs uppercase tracking-wider font-semibold text-slate-500 mb-2">
                    Tastatur-Shortcuts
                  </h3>
                  <table className="w-full text-sm">
                    <tbody>
                      {SHORTCUTS.map((s) => (
                        <tr
                          key={s.key}
                          className="border-b border-slate-100 dark:border-slate-800 last:border-0"
                        >
                          <td className="py-1.5 w-32">
                            <kbd className="kbd">{s.key}</kbd>
                          </td>
                          <td className="py-1.5 text-slate-600 dark:text-slate-300">
                            {s.desc}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>

                <section>
                  <h3 className="text-2xs uppercase tracking-wider font-semibold text-slate-500 mb-2">
                    Glossar
                  </h3>
                  <dl className="space-y-2">
                    {GLOSSAR.map((g) => (
                      <div key={g.term} className="flex gap-3">
                        <dt className="w-32 shrink-0 text-sm font-semibold text-slate-900 dark:text-slate-100">
                          {g.term}
                        </dt>
                        <dd className="text-sm text-slate-600 dark:text-slate-400 flex-1">
                          {g.desc}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </section>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
}
